import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { getMyPayrollAuthorities, savePayrollAuthorities } from '../../api/payrollAuthorities';

const validationSchema = yup.object({
  payroll_analyst: yup.string().optional(),
  payroll_officer: yup.string().optional(),
  payroll_manager: yup.string().optional(),
  director_finance_dept: yup.string().optional(),
  president: yup.string().optional(),
});

export default function SetPayrollAuthoritiesDialog({ open, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [currentAuthorities, setCurrentAuthorities] = useState(null);
  const [formInitialValues, setFormInitialValues] = useState({
    payroll_head: '',
    payroll_manager: '',
    director_finance_dept: '',
    president: '',
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        console.log('Submitting values:', values);
        const response = await savePayrollAuthorities(values);
        
        if (response.status === 'success') {
          enqueueSnackbar('Payroll authorities saved successfully', { variant: 'success' });
          setCurrentAuthorities(response.data);
          onClose();
        } else {
          enqueueSnackbar(response.message || 'Failed to save payroll authorities', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error saving payroll authorities:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to save payroll authorities';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  // Fetch current authorities when dialog opens
  useEffect(() => {
    if (open) {
      fetchAuthorities();
    }
  }, [open]);

  const fetchAuthorities = async () => {
    setFetching(true);
    try {
      const response = await getMyPayrollAuthorities();
      
      if (response.status === 'success' && response.data) {
        const authorities = response.data;
        setCurrentAuthorities(authorities);
        
        // Update form initial values with fetched data
        const newValues = {
          payroll_head: authorities.payroll_head || '',
          payroll_manager: authorities.payroll_manager || '',
          director_finance_dept: authorities.director_finance_dept || '',
          president: authorities.president || '',
        };
        setFormInitialValues(newValues);
      } else {
        // Initialize with empty values if no authorities found
        const emptyValues = {
          payroll_head: '',
          payroll_manager: '',
          director_finance_dept: '',
          president: '',
        };
        setFormInitialValues(emptyValues);
      }
    } catch (error) {
      console.error('Error fetching payroll authorities:', error);
      enqueueSnackbar(
        error.response?.data?.message || error.message || 'Failed to load payroll authorities',
        { variant: 'error' }
      );
      // Initialize with empty values on error
      const emptyValues = {
        payroll_analyst: '',
        payroll_officer: '',
        payroll_manager: '',
        director_finance_dept: '',
        president: '',
      };
      setFormInitialValues(emptyValues);
    } finally {
      setFetching(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      formik.resetForm();
      setCurrentAuthorities(null);
      setFormInitialValues({
        payroll_analyst: '',
        payroll_officer: '',
        payroll_manager: '',
        director_finance_dept: '',
        president: '',
      });
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="set-payroll-authorities-title"
      aria-describedby="set-payroll-authorities-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="set-payroll-authorities-title">
        Set Payroll Authorities
      </DialogTitle>
      <DialogContent sx={{ width: '100%', position: 'relative', minHeight: '400px' }}>
        {fetching ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              gap: 2,
            }}
          >
            <CircularProgress size={40} sx={{ color: '#12C1DC' }} />
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Loading current authorities...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            {currentAuthorities && (
              <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Current Authorities:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666' }}>
                  {currentAuthorities.payroll_head && `Payroll Head: ${currentAuthorities.payroll_head}`}
                  {currentAuthorities.payroll_head && <br />}
                  {currentAuthorities.payroll_manager && `Payroll Manager: ${currentAuthorities.payroll_manager}`}
                  {currentAuthorities.payroll_manager && <br />}
                  {currentAuthorities.director_finance_dept && `Director Finance Dept: ${currentAuthorities.director_finance_dept}`}
                  {currentAuthorities.director_finance_dept && <br />}
                  {currentAuthorities.president && `President: ${currentAuthorities.president}`}
                  {!currentAuthorities.payroll_analyst && !currentAuthorities.payroll_officer && 
                   !currentAuthorities.payroll_manager && !currentAuthorities.director_finance_dept && 
                   !currentAuthorities.president && 'No authorities set'}
                </Typography>
              </Box>
            )}

            <form onSubmit={formik.handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                <TextField
                  size="small"
                  label="Payroll Head"
                  name="payroll_head"
                  value={formik.values.payroll_head}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.payroll_head && Boolean(formik.errors.payroll_head)}
                  helperText={formik.touched.payroll_head && formik.errors.payroll_head}
                  fullWidth
                  disabled={loading}
                />

                <TextField
                  size="small"
                  label="Payroll Manager"
                  name="payroll_manager"
                  value={formik.values.payroll_manager}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.payroll_manager && Boolean(formik.errors.payroll_manager)}
                  helperText={formik.touched.payroll_manager && formik.errors.payroll_manager}
                  fullWidth
                  disabled={loading}
                />

                <TextField
                  size="small"
                  label="Director Finance Dept."
                  name="director_finance_dept"
                  value={formik.values.director_finance_dept}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.director_finance_dept && Boolean(formik.errors.director_finance_dept)}
                  helperText={formik.touched.director_finance_dept && formik.errors.director_finance_dept}
                  fullWidth
                  disabled={loading}
                />

                <TextField
                  size="small"
                  label="President"
                  name="president"
                  value={formik.values.president}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.president && Boolean(formik.errors.president)}
                  helperText={formik.touched.president && formik.errors.president}
                  fullWidth
                  disabled={loading}
                />

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || formik.isSubmitting}
                    sx={{ flex: 1 }}
                  >
                    {loading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={20} sx={{ color: 'inherit' }} />
                        <span>Saving...</span>
                      </Box>
                    ) : (
                      'Save'
                    )}
                  </Button>
                </Box>
              </Box>
            </form>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

